import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import { MdDeleteOutline } from "react-icons/md";
import { GiChart } from "react-icons/gi";
import { RiDraggable } from "react-icons/ri";
import MessageBox from './shared/MessageBox';
import { exctractDataFromFiles } from '../lib/utilities/exctractDataFromFiles';
import { xyExtraction } from '../lib/utilities/xyExtraction';
import { calculateStdLevelAPI } from '../api/api';
import { IoCloudUploadOutline } from "react-icons/io5";
import Loader from './shared/Loader';
import { Toaster, toast } from 'react-hot-toast';





const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '60px',
  borderWidth: 2,
  borderRadius: 6,
  borderColor: '#bdbdbd',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

const focusedStyle = {
  borderColor: '#2196f3'
};

const acceptStyle = {
  borderColor: '#00e676'
};

const rejectStyle = {
  borderColor: '#ff1744'
};

const bgColors = [
  // 'bg-cyan-200',
  // 'bg-cyan-300',
  // 'bg-cyan-400',
  // 'bg-cyan-500',
  // 'bg-cyan-600',
  // 'bg-cyan-700',
  // 'bg-cyan-800',
  // 'bg-cyan-900',
  // 'bg-cyan-950',
  'bg-white'
];


function DropZone(props) {

  const { loadingProgress = {}, enableReorder = false } = props;

  const [data, setData] = useState([]);
  const DROPPABLE_ID = 'files-droppable';

  // DnD via React Beautiful DND
  const StrictModeDroppable = ({ children, ...droppableProps }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
      const animation = requestAnimationFrame(() => setEnabled(true));
      return () => {
        cancelAnimationFrame(animation);
        setEnabled(false);
      };
    }, []);
    if (!enabled) return null;
    return <Droppable {...droppableProps}>{children}</Droppable>;
  };
  
  const [files, setFiles] = useState([]);
  const [preparing, setPreparing] = useState(false);

  // Keep a stable droppable id to avoid DnD invariant errors
  useEffect(() => {}, [data])

  // const backgroundColorClasses = useMemo(() => {

  //     return files.map((_, index) => {
  //       const colorIndex = Math.min(index, bgColors.length - 1);
  //       return bgColors[colorIndex];
  //     });
    
  // }, [files]);

  // const handleFileChange = async (files) => {
  //   const file = files[0];

  //   if (file) {
  //     try {
  //       const fileContent = await readFile(file);
  //       const lines = fileContent.split('\n');

  //       // Find the start and end indices of the chromatogram data
  //       const startIndex = lines.findIndex(line => line.startsWith('Time (min)')) + 1;
  //       const endIndex = lines.length;

  //       // Extract the relevant data from the chromatogram section
  //       const chromatogramData = lines.slice(startIndex, endIndex).map(line => line.split('\t'));

  //       // Create an array of objects with x and y values
  //       const resultArray = chromatogramData.map(data => ({ x: parseFloat(data[0]), y: parseFloat(data[2]) }));

  //       setData(resultArray);
  //       // props.extractData(resultArray);
  //     } catch (error) {
  //       console.error('Error reading or parsing file:', error);
  //     }
  //   }
  // };

  // const readFile = (file) => {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onload = (event) => {
  //       resolve(event.target.result);
  //     };

  //     reader.onerror = (error) => {
  //       reject(error);
  //     };

  //     reader.readAsText(file);
  //   });
  // };

    const initialFilesManipultion = async(acceptedFiles) => {
      const resultDataArray = await exctractDataFromFiles(acceptedFiles);
      const resultDataArrayWithXYExtraction = await xyExtraction(resultDataArray);


      const transformedData = resultDataArrayWithXYExtraction.reduce((acc, item) => {
        acc[item.name] = {
          x: item.originalXValues,
          y: item.originalYValues,
        };
        return acc;
      }, {});
    
      try {
          const response = await calculateStdLevelAPI(transformedData)

              // setFileLevels(response.data);
              const fileLevels = response;

              // Map files to include their levels
              const filesWithLevels = acceptedFiles.map(file => ({
                file,
                level: fileLevels[file.name].level
              }));

              // Sort the files based on the level
              filesWithLevels.sort((a, b) => a.level - b.level);

              // Extract the original files from the sorted array
              // const finalFiles = filesWithLevels.map(item => item.file);
              const finalFiles = filesWithLevels.map(item => {
                const file = item.file;
                file.level = item.level;
                return file;
              });


              setData(finalFiles);
              setFiles(finalFiles);
              props.handleInputFiles(finalFiles);

      } catch (error) {
          console.error('Error:', error);
      }


    }



  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject
  } = useDropzone({
    accept: {
      'text/plain': ['.txt']
    },
    onDropRejected: (fileRejections) => {
      toast.error("Only .txt files are allowed.");
    },
    onDrop: async (acceptedFiles) => {
      setPreparing(true); // start showing overlay
      await initialFilesManipultion(acceptedFiles);
      setPreparing(false); // hide overlay once done
    }
  });


  const style = useMemo(() => ({
    ...baseStyle,
    ...(isFocused ? focusedStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isFocused,
    isDragAccept,
    isDragReject
  ]);


  // Keep local list in sync with initialFiles for edge cases:
  // - Seed once when empty and initialFiles has content
  // - Clear local files if parent clears initialFiles (e.g., after Restart)
  useEffect(() => {
    const init = Array.isArray(props.initialFiles) ? props.initialFiles : [];
    if (files.length === 0 && init.length > 0) {
      setFiles(init);
    } else if (init.length === 0 && files.length > 0) {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialFiles]);

  const handleOnDragEnd = (result) => {
    if (!enableReorder) return;
    if (!result.destination) return;
    const items = Array.from(files);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFiles(items);
    props.handleInputFiles(items);
  }

  const handleDeleteFile = (id) => {
    const updatedFiles = files.filter(item => (item.path || item.name) !== id)
    setFiles(updatedFiles);
    props.handleInputFiles(updatedFiles);
  }

  return (
    <div className='mx-48 my-1 relative'>
      <Toaster position="top-center"/>
      {preparing && (
        <Loader text={'Preparing Files'} />
      )}
      <div {...getRootProps({style})}>
        <input {...getInputProps()} />
        <IoCloudUploadOutline className='text-5xl mb-4'/>
        <p>Drag 'n' drop your Files here, or click to select files</p>
        <p className='italic'>(Only *.txt files will be accepted)</p>
      </div>
      {acceptedFiles.length > 0 && props.showHelpText && 
        <MessageBox message={'We have sorted the order of files automaticaly from Low to High - Top to Bottom concentration. You can change the order manually by drag and drop.'} />
      }
      {enableReorder ? (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <StrictModeDroppable droppableId={DROPPABLE_ID}>
            {(provided) => (
              <ul className='mt-6 flex-1 max-h-[480px] overflow-y-auto px-2' {...provided.droppableProps} ref={provided.innerRef}>
                {files.map((file, index) => {
                  const id = String(file.path || file.name || index);
                  const displayName = String(file.path || file.name || '').split('.').slice(0, -1).join('.') || String(file.path || file.name || '');
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(provided) => (
                        <li
                          className="relative my-3 rounded-lg shadow-md border overflow-hidden"
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                        >
                          <div
                            className="absolute top-0 left-0 h-full transition-all duration-500 ease-out"
                            style={{
                              width: loadingProgress[file.name] === null || loadingProgress[file.name] === undefined ? '100%' : `${Math.min(100, loadingProgress[file.name])}%`,
                              backgroundColor: loadingProgress[file.name] === null || loadingProgress[file.name] === undefined ? 'white' : '#6ECD69',
                              zIndex: 0
                            }}
                          ></div>
                          <div className="relative z-0 flex justify-between p-3 items-center">
                            <div className='flex flex-row items-center'>
                              <RiDraggable fontSize={22} color='gray' />
                              <div className='ml-4 flex flex-col'>
                                <span>{displayName}</span>
                              </div>
                            </div>
                            {(loadingProgress[file.name] === null || loadingProgress[file.name] === undefined) && (
                              <div className='flex justify-center items-center'>
                                {props.level && <span className='mr-4'>level: {index + 1}</span>}
                                <button onClick={() => props.handleFileName(file.name)} className='text-sm text-cyan-950 mr-4 p-1 rounded bg-white shadow hover:bg-green-200'>
                                  <GiChart fontSize={24} color='#14532d' />
                                </button>
                                <button className='bg-white shadow p-1 rounded hover:bg-red-200' onClick={() => handleDeleteFile(id)}>
                                  <MdDeleteOutline fontSize={24} color='#b91c1c' />
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </ul>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      ) : (
        <ul className='mt-6 flex-1 max-h-[480px] overflow-y-auto px-2'>
          {files.map((file, index) => {
            const id = String(file.path || file.name || index);
            const displayName = String(file.path || file.name || '').split('.').slice(0, -1).join('.') || String(file.path || file.name || '');
            return (
              <li key={id} className="relative my-3 rounded-lg shadow-md border overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full transition-all duration-500 ease-out"
                  style={{
                    width: loadingProgress[file.name] === null || loadingProgress[file.name] === undefined ? '100%' : `${Math.min(100, loadingProgress[file.name])}%`,
                    backgroundColor: loadingProgress[file.name] === null || loadingProgress[file.name] === undefined ? 'white' : '#6ECD69',
                    zIndex: 0
                  }}
                ></div>
                <div className="relative z-0 flex justify-between p-3 items-center">
                  <div className='flex flex-row items-center'>
                    <div className='ml-0 flex flex-col'>
                      <span>{displayName}</span>
                    </div>
                  </div>
                  {(loadingProgress[file.name] === null || loadingProgress[file.name] === undefined) && (
                    <div className='flex justify-center items-center'>
                      {props.level && <span className='mr-4'>level: {index + 1}</span>}
                      <button onClick={() => props.handleFileName(file.name)} className='text-sm text-cyan-950 mr-4 p-1 rounded bg-white shadow hover:bg-green-200'>
                        <GiChart fontSize={24} color='#14532d' />
                      </button>
                      <button className='bg-white shadow p-1 rounded hover:bg-red-200' onClick={() => handleDeleteFile(id)}>
                        <MdDeleteOutline fontSize={24} color='#b91c1c' />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  );
}

export default DropZone
